import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { Label, FormFeedback } from "reactstrap";
import axios from "../../../api/axios";
import '../../parking.scss';

const slotSchema = Yup.object().shape({
    slot_no: Yup.string().required("Slot number is required"),
    suite_no: Yup.string().required("Suite number is required"),
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile_no: Yup.string().required("Mobile number is required"),
    vehicle_no: Yup.string().required("Vehicle number is required"),
    amount: Yup.number().positive("Amount must be positive").required("Amount is required"),
    type: Yup.string()
        .oneOf(["Regular", "Visitor Parking", "Handicap"], "Invalid type selected")
        .required("Type is required"),
    notes: Yup.string().required("Notes is required"),
});

const SlotAllocationForm = ({ isOpen, toggle, property, layout_id, slot_no }) => {
    const defaultValues = {
        slot_no,
        suite_no: "", name: "", email: "",
        mobile_no: "", vehicle_no: "", amount: "",
        type: "", notes: "",
    };

    const [initialValues, setInitialValues] = useState(defaultValues);

    useEffect(() => {
        if (property) {
            axios
                .get(`/parking_new/slots/slot-details`, {
                    params: { property_id: property, layout_id, slot_no },
                })
                .then((response) => {
                    setInitialValues(response.data || defaultValues);
                })
                .catch(() => setInitialValues(defaultValues));
        }
    }, [property, isOpen]);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            await axios.post(`/parking_new/slots/add-or-update`, {
                property_id: property,
                layout_id,
                slot_data: values,
            });
            toggle();
        } catch (error) {
            console.error("Error saving parking slot:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveSlot = async () => {
        try {
            await axios.delete(`/parking_new/slots/remove-slot`, {
                data: { property_id: property, layout_id, slot_no },
            });
            toggle();
        } catch (error) {
            console.error("Error removing parking slot:", error);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className="parking-modal">
            <ModalHeader toggle={toggle}>
                <span className="text-white">Manage Parking Slot — {slot_no}</span>
            </ModalHeader>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={slotSchema}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, isSubmitting }) => (
                    <Form>
                        <ModalBody>
                            <div className="row">
                                {renderField("slot_no", "Slot Number", "text", errors, touched, [], 6, true)}
                                {renderField("suite_no", "Suite Number", "text", errors, touched, [], 6)}
                            </div>
                            <div className="row">
                                {renderField("name", "Name", "text", errors, touched, [], 6)}
                                {renderField("email", "Email", "email", errors, touched, [], 6)}
                            </div>
                            <div className="row">
                                {renderField("mobile_no", "Mobile Number", "text", errors, touched, [], 6)}
                                {renderField("vehicle_no", "Vehicle Number", "text", errors, touched, [], 6)}
                            </div>
                            <div className="row">
                                {renderField("amount", "Amount", "number", errors, touched, [], 6)}
                                {renderField("type", "Type", "select", errors, touched, [
                                    { value: "", label: "Select Type" },
                                    { value: "Regular", label: "Regular" },
                                    { value: "Visitor Parking", label: "Visitor Parking" },
                                    { value: "Handicap", label: "Handicap" },
                                ], 6)}
                            </div>
                            <div className="row">
                                {renderField("notes", "Notes", "textarea", errors, touched, [], 12)}
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={toggle}>Cancel</Button>
                            <Button color="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Saving…" : "Save Slot"}
                            </Button>
                            <Button color="danger" onClick={handleRemoveSlot} disabled={isSubmitting}>
                                Delete Slot
                            </Button>
                        </ModalFooter>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

const renderField = (name, label, type, errors, touched, options = [], colSize = 6, disabled = false) => {
    const error = errors[name];
    const isTouched = touched[name];

    return (
        <div className={`col-${colSize} mb-3`}>
            <Label htmlFor={name} className="form-label">{label}</Label>
            {type === "select" ? (
                <Field name={name}>
                    {({ field }) => (
                        <select
                            {...field}
                            className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                            disabled={disabled}
                        >
                            {options.map((option, idx) => (
                                <option key={idx} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    )}
                </Field>
            ) : type === "textarea" ? (
                <Field name={name}>
                    {({ field }) => (
                        <textarea
                            {...field}
                            rows={3}
                            className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                            disabled={disabled}
                        />
                    )}
                </Field>
            ) : (
                <Field name={name}>
                    {({ field }) => (
                        <input
                            {...field}
                            type={type}
                            className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                            disabled={disabled}
                        />
                    )}
                </Field>
            )}
            {error && isTouched && <FormFeedback className="d-block">{error}</FormFeedback>}
        </div>
    );
};

export { SlotAllocationForm };